import { Measurement, UNIT_IN_PIXELS } from "./constants";
import { depth$, inserts$, width$ } from "./state";
import { DimType, DirectionsObj, InsertData, Point, RectPoints } from "./types";

export const wholeNumber = (value: string | number): number => {
  if (typeof value === 'string') {
    return value.includes('.') ? parseInt(parseFloat(value).toFixed(2).replace('.', '')) : parseInt(value);
  } else if (typeof value === 'number' && value.toString().includes('.')) {
    return parseInt(value.toString().replace('.', ''));
  } else {
    return value;
  }
}

export const money = (value: number, currencySymbol = '$'): string => {
  return `${currencySymbol}${(value / 100).toFixed(2)}`;
}

export const deepClone = (obj: Object): Object => {
  return JSON.parse(JSON.stringify(obj));
}

export const generateInsertId = (): string => {
  return `insert-${Date.now()}`;
}

export const createDimensionalOptions = (type: DimType | 'insert-width' | 'insert-depth', unitType: 'whole' | 'fraction'): HTMLOptionElement[] => {
  const arr:HTMLOptionElement[] = [];
  let min = 0;
  let max = 0;
  let option: HTMLOptionElement;
  let value: string;
  let valueAsNumber: number;
  //
  if (type === 'width') {
    min = unitType === 'whole' ? Measurement.MIN_WIDTH : 0;
    max = unitType === 'whole' ? Measurement.MAX_WIDTH + 1 : Measurement.NUM_FRACTIONS;
  } else if (type === 'height') {
    min = unitType === 'whole' ? Measurement.MIN_HEIGHT : 0;
    max = unitType === 'whole' ? Measurement.MAX_HEIGHT + 1 : Measurement.NUM_FRACTIONS;
  } else if (type === 'depth') {
    min = unitType === 'whole' ? Measurement.MIN_DEPTH : 0;
    max = unitType === 'whole' ? Measurement.MAX_DEPTH + 1 : Measurement.NUM_FRACTIONS;
  } else if (type === 'insert-width') {
    min = unitType === 'whole' ? Measurement.MIN_INSERT_WIDTH : 0;
    max = unitType === 'whole' ? Measurement.MAX_WIDTH + 1 : Measurement.NUM_FRACTIONS;
  } else if (type === 'insert-depth') {
    min = unitType === 'whole' ? Measurement.MIN_INSERT_DEPTH : 0;
    max = unitType === 'whole' ? Measurement.MAX_DEPTH + 1 : Measurement.NUM_FRACTIONS;
  }
  
  for (let i = min; i < max; i++) {
    option = document.createElement('OPTION') as HTMLOptionElement;
    valueAsNumber = unitType === 'whole' ? (Measurement.UNIT_WHOLE * i) : (Measurement.UNIT_FRACTION * i);
    value = unitType === 'whole' ? valueAsNumber.toString() : valueAsNumber.toFixed(getFixedNum(valueAsNumber));
    option.value = value;
    option.textContent = unitType === 'whole' ?  `${value}”` : `${getFractionString(valueAsNumber.toFixed(getFixedNum(valueAsNumber)))}”`;
    arr.push(option);
  }
  return arr;
}

const fractFloatArr = [0.0625, 0.125, 0.1875, 0.25, 0.3125, 0.375, 0.4375, 0.5, 0.5625, 0.625, 0.75, 0.8125, 0.875, 0.9375]

const getFixedNum = (val: number): number => {
  const remainder = val.toString().split('.')[1];
  if (remainder?.length >= 4) {
    return 4
  } else if (remainder?.length == 3) {
    return 3
  } else if (remainder?.length == 2) {
    return 2;
  } else {
    return 1;
  }
}

export const getFractionString = (floatAsStr: string): string => {
  /* console.log('getFractionString', floatAsStr); */
  let str = '';
  switch (floatAsStr) {
    case '0.0625':
      str = ' 1/16'
      break;

    case '0.125':
      str = ' 1/8'
      break;

    case '0.1875':
      str = ' 3/16'
      break;

    case '0.25':
      str = ' 1/4'
      break;

    case '0.3125':
      str = ' 5/16'
      break;

    case '0.375':
      str = ' 3/8'
      break;

    case '0.4375':
      str = ' 7/16'
      break;
    
    case '0.5':
      str = ' 1/2'
      break;

    case '0.5625':
      str = ' 9/16'
      break;

    case '0.625':
      str = ' 5/8'
      break;

    case '0.6875':
      str = ' 11/16'
      break;

    case '0.75':
      str = ' 3/4'
      break;

    case '0.8125':
      str = ' 13/16'
      break;

    case '0.875':
      str = ' 7/8'
      break;

    case '0.9375':
      str = ' 15/16'
      break;

    case '0.0':
      str = '0'
      break;

    default:
      const float = parseFloat(floatAsStr);
      str = getFractionString(
        fractFloatArr.map(num => ({num, dif: Math.abs(num - float)}))
        .reduce((a,b) => a.dif < b.dif ? a : b).num.toString()
      );
      break;
  }
  return str;
}

export const getFancySingleDimString = (unit: number): string => {
  const unitArr = unit.toString().split('.');
  let d = '';
  unitArr.forEach((str, i) => {
    d = d.concat(i === 0 ? str : getFractionString(`0.${str}`))
  });
  return `${d}”`;
}

export const getFancyDimString = (unitWidth: number, unitHeight: number): string => {
  /* console.log('getFancyDimString', unitWidth, unitHeight); */
  return `${getFancySingleDimString(unitWidth)} x ${getFancySingleDimString(unitHeight)}`;
}

export const rectContains = (a: RectPoints, b: RectPoints): boolean => {
  return !(
		b.x1 < a.x1 ||
		b.y1 < a.y1 ||
		b.x2 > a.x2 ||
		b.y2 > a.y2
	);
}

export const rectOverlaps = (a: RectPoints, b: RectPoints): boolean => {
  // no horizontal overlap
	if (a.x1 >= b.x2 || b.x1 >= a.x2) return false;
	// no vertical overlap
	if (a.y1 >= b.y2 || b.y1 >= a.y2) return false;
  //
	return true;
}

export const isInIframe = () => {
  return window.self !== window.top;
}

export const getNearestInsertDataLeft = (insert: InsertData): InsertData | null => {
  let rectA: RectPoints;
  const rectB: RectPoints = {
    x1: 0, 
    x2: insert.x,
    y1: insert.y + 1,
    y2: insert.y + insert.height - 1
  };
  const filtered = inserts$.value.filter(dta => dta.id !== insert.id).filter((dta) => {
    rectA = {
      x1: dta.x, 
      x2: dta.x + dta.width,
      y1: dta.y,
      y2: dta.y + dta.height
    };
    return rectContains(rectA,rectB) || rectOverlaps(rectA,rectB); 
  });
  //
  return filtered.length ? filtered.reduce((a,b) => (a.x + a.width) > (b.x + b.width) ? a : b) : null;
}

export const getNearestInsertDataRight = (insert: InsertData, gridWidth: number): InsertData | null => {
  let rectA: RectPoints;
  const rectB: RectPoints = {
    x1: insert.x + insert.width, 
    x2: gridWidth,
    y1: insert.y + 1,
    y2: insert.y + insert.height - 1
  };
  const filtered = inserts$.value.filter(dta => dta.id !== insert.id).filter((dta) => {
    rectA = {
      x1: dta.x, 
      x2: dta.x + dta.width,
      y1: dta.y,
      y2: dta.y + dta.height
    };
    return (rectContains(rectA,rectB) || rectOverlaps(rectA,rectB)) ; 
  });
  //
  return filtered.length ? filtered.reduce((a,b) => a.x < b.x ? a : b) : null;
}

export const getNearestInsertDataUp = (insert: InsertData): InsertData | null => {
  let rectA: RectPoints;
  const rectB: RectPoints = {
    x1: insert.x + 1, 
    x2: insert.x + insert.width - 1,
    y1: 0,
    y2: insert.y
  };
  const filtered = inserts$.value.filter(dta => dta.id !== insert.id).filter((dta) => {
    rectA = {
      x1: dta.x, 
      x2: dta.x + dta.width,
      y1: dta.y,
      y2: dta.y + dta.height
    };
    return (rectContains(rectA,rectB) || rectOverlaps(rectA,rectB)) /* && rectA.y2 <= rectB.y2 */; 
  });
  //
  return filtered.length ? filtered.reduce((a,b) => (a.y + a.height) > (b.y + b.height) ? a : b) : null;
}

export const getNearestInsertDataDown = (insert: InsertData, depth: number): InsertData | null => {
  let rectA: RectPoints;
  const rectB: RectPoints = {
    x1: insert.x + 1, 
    x2: insert.x + insert.width - 1,
    y1: insert.y + insert.height,
    y2: depth
  };
  const filtered = inserts$.value.filter(dta => dta.id !== insert?.id).filter((dta) => {
    rectA = {
      x1: dta.x, 
      x2: dta.x + dta.width,
      y1: dta.y,
      y2: dta.y + dta.height
    };
    return (rectContains(rectA,rectB) || rectOverlaps(rectA,rectB)) /* && rectA.y1 >= rectB.y1 */; 
  });
  return filtered.length ? filtered.reduce((a,b) => a.y < b.y ? a : b) : null;
}

export const getDistance2D = (a: Point, b: Point): number => {
  return Math.sqrt( Math.pow((a.x - b.x), 2) + Math.pow((a.y - b.y), 2) )
}

export const getOverlap = (a: RectPoints, b: RectPoints ): number => {
  return (Math.max(a.x1,b.x1) - Math.min(a.x2,b.x2)) * (Math.max(a.y1,b.y1) - Math.min(a.y2,b.y2))
}

export const getPercentAreaCovered = (inserts: InsertData[]): number => {
  let iRectPoints: RectPoints;
  const trayRectPoints: RectPoints = {
    x1: 0,
    x2: (width$.value * UNIT_IN_PIXELS) - Measurement.SEP_WIDTH_PX,
    y1: 0,
    y2: (depth$.value * UNIT_IN_PIXELS) - Measurement.SEP_WIDTH_PX
  }
  const trayArea = trayRectPoints.x2 * trayRectPoints.y2;
  const insertsArea = inserts.map(insert => {
    iRectPoints = {
      x1: insert.x, 
      x2: insert.x + insert.width,
      y1: insert.y,
      y2: insert.y + insert.height
    }
    return getOverlap(trayRectPoints, iRectPoints);
  }).reduce((a,b) => a + b);
  //
  return insertsArea/trayArea;
}

export const updateInsertDataToFillGaps = (insertData: InsertData, directions: DirectionsObj, tolerance: number = Measurement.UNIT_FRACTION_DISPLAYED_PX ): { data: InsertData, hasChanges: boolean } =>  {
  const data = { ...insertData };
  let hasChanges = false;
  let closestInsertData: InsertData | null;
  let gap: number;

  if (data.type !== 'cushion' && data.type !== 'pillow') {
    // gap right
    if (directions.right) {
      const right = data.x + data.width;
      const gridWidth = (width$.value * UNIT_IN_PIXELS) - Measurement.SEP_WIDTH_PX;
      closestInsertData = getNearestInsertDataRight(data, gridWidth);
      gap = closestInsertData ? closestInsertData.x - right : gridWidth - right;
      if (gap !== 0 && gap <= tolerance) {
        /* console.log('right gap', gap); */
        data.width += gap;
        data.unitWidth = data.width / UNIT_IN_PIXELS;
        hasChanges = true;
      }
    }

    // gap left
    if (directions.left) {
      closestInsertData = getNearestInsertDataLeft(data);
      gap = closestInsertData ? data.x - (closestInsertData.x + closestInsertData.width) : data.x;
      if (gap !== 0 && gap <= tolerance) {
        /* console.log('left gap', gap); */
        data.width += gap;
        data.unitWidth = data.width / UNIT_IN_PIXELS;
        data.x = closestInsertData ? closestInsertData.x + closestInsertData.width : 0;
        hasChanges = true;
      }
    }
    
    // gap top
    if (directions.top) {
      closestInsertData = getNearestInsertDataUp(data);
      gap = closestInsertData ? data.y - (closestInsertData.y + closestInsertData.height) : data.y;
      if (gap !== 0 && gap <= tolerance) {
        /* console.log('top gap', gap); */
        data.height += gap;
        data.unitHeight = data.height / UNIT_IN_PIXELS;
        data.y = closestInsertData ? closestInsertData.y + closestInsertData.height : 0;
        hasChanges = true;
      }
    }
    
    // gap bottom
    if (directions.bottom) {
      const bottom = data.y + data.height;
      const gridDepth = (depth$.value * UNIT_IN_PIXELS) - Measurement.SEP_WIDTH_PX;
      closestInsertData = getNearestInsertDataDown(data, gridDepth);
      gap = closestInsertData ? closestInsertData.y - bottom : gridDepth - bottom;
      if (gap !== 0 && gap <= tolerance) {
        /* console.log('bottom gap', gap); */
        data.height += gap;
        data.unitHeight = data.height / UNIT_IN_PIXELS;
        hasChanges = true;
      }
    }
  }
  
  return { data, hasChanges}
}